// Freshness-Store: per-Geraet adaptiver Online-Schwellwert.
//
// Backend liefert pro (domain, device_id) das gemessene P95 der Gaps
// zwischen aufeinanderfolgenden MQTT-Reports plus einen 3x-Multiplier
// + 5-min-Floor als threshold_s. Frontend nutzt das, um "online" je
// Geraet richtig zu setzen -- ein Battery-Door-Sensor mit 4-h-
// Heartbeat darf nach 6 h offline gehen, eine Watt-Steckdose mit
// 5-s-Reports nach 5 min.
//
// Fallbacks per Domain wenn noch kein Wert da ist (frisches Geraet,
// Cold-Start). Eher grosszuegig als zu strikt -- "neu, unbekannt"
// soll nicht sofort als offline rot leuchten.
//
// Nur die Geräte-Seite liest die Schwellen -- darum „Wer liest, holt“
// (lib/bei-bedarf.ts, seit 03.11.2026): Abruf und 60-s-Takt nur, solange
// sie offen ist (beobachteFreshness); start* spielt nur die Ablage ein.
// Vorher holte jeder Tab ~5 kB/min für niemanden.

import { untrack } from "svelte";
import { SvelteMap } from "svelte/reactivity";
import { beiBedarf } from "./bei-bedarf";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { registerRevalidator } from "./revalidate.svelte";

export interface FreshnessEntry {
  domain: string;
  device_id: string;
  p95_interval_s: number;
  threshold_s: number;
  sample_count: number;
  source: "live" | "persisted" | string;
}

const STORAGE_KEY = "freshness";

const _byKey = new SvelteMap<string, FreshnessEntry>();
let _started = false;

function key(domain: string, deviceID: string): string {
  return `${domain}::${deviceID}`;
}

async function refresh(): Promise<void> {
  try {
    const r = await fetch("/v2-api/freshness", { credentials: "include" });
    if (!r.ok) return;
    const list = (await r.json()) as FreshnessEntry[];
    uebernimm(list);
    persist(STORAGE_KEY, list);
  } catch {
    // ignore -- naechster Tick versucht's wieder
  }
}

function uebernimm(list: FreshnessEntry[]): void {
  _byKey.clear();
  for (const e of list) _byKey.set(key(e.domain, e.device_id), e);
}

// 60 s reicht: P95 aendert sich langsam, der Daily-Recompute schreibt
// sowieso nur einmal pro Tag rein.
const _bedarf = beiBedarf({ hole: refresh, takt: 60_000 });

export function startFreshness(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(() => {
    if (_bedarf.aktiv()) void refresh();
  });
  const cached = loadPersisted<FreshnessEntry[]>(STORAGE_KEY);
  if (Array.isArray(cached)) uebernimm(cached);
}

// Anmelden, solange die Geräte-Seite offen ist; der Rückgabewert meldet ab.
// In Svelte: $effect(() => { if (zuSehen()) return beobachteFreshness(); }).
export function beobachteFreshness(): () => void {
  return untrack(() => {
    startFreshness();
    return _bedarf.beobachte();
  });
}

export function stopFreshness(): void {
  _started = false;
  _bedarf.beende();
  _byKey.clear();
}

// Default-Threshold pro Domain wenn kein gemessener Wert vorliegt.
// Bewusst grosszuegig -- ein neu eingebundenes Geraet soll nicht
// instant als offline gelten, nur weil noch keine 50 Reports da sind.
const DEFAULTS_S: Record<string, number> = {
  zigbee: 12 * 3600, // 12 h fuer Aktoren/Battery-Devices
  zwave: 30 * 60, // 30 min, mains-powered
  lipoguard: 30 * 60, // 30 min
  co2monitor: 30 * 60, // 30 min
};
const DEFAULT_FALLBACK_S = 6 * 3600; // 6 h fuer alles unbekannte

export function freshnessThresholdSec(
  domain: string,
  deviceID: string,
): number {
  const e = _byKey.get(key(domain, deviceID));
  if (e && e.threshold_s > 0) return e.threshold_s;
  return DEFAULTS_S[domain] ?? DEFAULT_FALLBACK_S;
}

export function getFreshness(
  domain: string,
  deviceID: string,
): FreshnessEntry | undefined {
  return _byKey.get(key(domain, deviceID));
}

// isFresh prueft ob lastSeenIso (oder unix ms / sec) innerhalb der
// adaptiven Schwelle liegt. now ist optional fuer Tests; default
// ist Date.now(). Returnt true/false oder null wenn lastSeen fehlt.
export function isFresh(
  domain: string,
  deviceID: string,
  lastSeenMs: number | null,
  nowMs?: number,
): boolean | null {
  if (lastSeenMs == null) return null;
  const now = nowMs ?? Date.now();
  const thresholdMs = freshnessThresholdSec(domain, deviceID) * 1000;
  return now - lastSeenMs < thresholdMs;
}
